import { useState } from 'react';
import { useSubpantalla } from '../ui/navegacionReact';
import { LayoutDashboard, Building2, FileSearch, LogOut, ShieldCheck, MessageSquare } from 'lucide-react';
import { useAuthPanel } from './contextoPanel';
import Acceso from './Acceso';
import Metricas from './Metricas';
import Clientes from './Clientes';
import FichaCliente from './FichaCliente';
import AuditoriaPanel from './AuditoriaPanel';
import Interesados from './Interesados';

const SECCIONES = [
  { id: 'metricas', etiqueta: 'Estado del servicio', icono: LayoutDashboard },
  { id: 'clientes', etiqueta: 'Clientes', icono: Building2 },
  // VA ANTES DE LA BITÁCORA y justo debajo de Clientes: es lo que se
  // mira todos los días, y la bitácora es lo que se mira cuando pasa
  // algo. El orden de una barra lateral es una recomendación de en qué
  // fijarse primero, aunque nadie lo diga.
  { id: 'interesados', etiqueta: 'Interesados', icono: MessageSquare },
  { id: 'auditoria', etiqueta: 'Bitácora', icono: FileSearch },
];

// Consola del panel de plataforma.
//
// El armazón es OSCURO, y no por gusto: es la señal de que quien mira está
// en el panel del servicio y no en la consola de una empresa. Confundir las
// dos —creer que estás viendo tus datos cuando administras los de otro— es
// el error caro de esta pantalla, y el color es la defensa más barata
// contra él.
export default function PanelApp() {
  const { autenticado, admin, cerrarSesion } = useAuthPanel();
  const [seccion, setSeccion] = useState('metricas');
  const [idCliente, setIdCliente] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  // ATRÁS EN EL PANEL · TC-DR-AT. La ficha de un cliente y la sección que
  // no es la de inicio ocupan su entrada: atrás cierra la ficha, y después
  // vuelve a Métricas. Desde Métricas, atrás sale del panel, como siempre
  // (esta aplicación no lleva aviso de salida).
  //
  // La ficha va PRIMERO: al cambiar de sección con una ficha abierta, su
  // entrada se consume antes de que la sección escriba la suya.
  useSubpantalla('cliente', !!idCliente, () => setIdCliente(null), { valor: idCliente, abrir: setIdCliente });
  useSubpantalla('seccion', seccion !== 'metricas', () => setSeccion('metricas'),
    { valor: seccion, abrir: setSeccion, reemplaza: true });

  if (!autenticado) return <Acceso />;

  function irA(id) {
    setIdCliente(null);
    setSeccion(id);
  }

  return (
    <div className="min-h-dvh bg-gray-50 flex">
      <aside className="w-60 shrink-0 bg-slate-900 text-slate-300 flex flex-col">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-400" />
            <span className="font-bold text-white tracking-tight">
              TEGO<span className="text-blue-400"> Plataforma</span>
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Panel del operador del servicio</p>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {SECCIONES.map((s) => {
            const Icono = s.icono;
            const activa = seccion === s.id;
            return (
              <button
                key={s.id}
                onClick={() => irA(s.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium ${
                  activa ? 'bg-blue-600 text-white' : 'hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icono className="h-4 w-4" /> {s.etiqueta}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-2 pb-2">
            <div className="text-sm font-semibold text-white truncate">{admin?.nombre}</div>
            <div className="text-[11px] text-slate-500 truncate">{admin?.email}</div>
          </div>
          <button
            onClick={cerrarSesion}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Salir
          </button>
          <a
            href="/"
            className="block px-3 py-2 text-[11px] text-slate-500 hover:text-slate-300"
          >
            ← Ir al sistema de inventario
          </a>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Banda permanente. No es decorativa: recuerda en todo momento que
            lo que se ve pertenece a otras empresas, no a la propia. */}
        <div className="bg-slate-800 text-slate-300 text-xs px-6 py-1.5">
          Estás administrando <strong className="text-white">datos de clientes</strong>.
          El inventario de cada empresa no es accesible desde aquí.
        </div>

        <div className="p-6 max-w-7xl">
          {seccion === 'metricas' && (
            <Metricas
              irACliente={(estado) => {
                setFiltroEstado(estado);
                setSeccion('clientes');
              }}
            />
          )}

          {seccion === 'clientes' &&
            (idCliente ? (
              <FichaCliente id={idCliente} onVolver={() => setIdCliente(null)} />
            ) : (
              <Clientes estadoInicial={filtroEstado} onAbrir={setIdCliente} />
            ))}

          {seccion === 'interesados' && <Interesados />}

          {seccion === 'auditoria' && <AuditoriaPanel />}
        </div>
      </main>
    </div>
  );
}
